import { useMutation } from "@tanstack/react-query";
import { API } from "../../API";
import { callNumber } from "../../external_link";
import { isLoading, Loading } from "../../Loading";
import { AgencyId } from "../../messages";
import { STRINGS } from "../../strings";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { translate } from "../../translate";
import { ModalActions, useModalV2 } from "../AppModals";
import { useMedflytAgencyId } from "../withMedflytAgencyId";

export function useCallNumber(props: { api: API; caregiver: AppCaregiver | Loading }) {
  const { createModal } = useModalV2();
  const medflytAgencyId = useMedflytAgencyId();

  const call = useMutation({
    mutationFn: async (params: { source: string; destination: string }) => {
      if (isLoading(medflytAgencyId)) return null;

      await props.api.createTelephonyCall(medflytAgencyId, {
        source: params.source,
        destination: params.destination,
      });

      return;
    },
  });

  const toNational = (e164: string) => {
    if (e164.startsWith("+972")) {
      return `0${e164.slice(4, 6)}-${e164.slice(6, 9)}-${e164.slice(9)}`;
    }

    if (e164.startsWith("+1")) {
      return `(${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}`;
    }

    return e164;
  };

  return (params: {
    agencyId: AgencyId;
    destination: string;
    name: string;
    onClickCall?: () => void;
    onClickCancel?: () => void;
  }) => {
    if (isLoading(props.caregiver)) return;
    if (isLoading(medflytAgencyId)) return;
    if (params.agencyId !== medflytAgencyId) return callNumber(params.destination);

    const source = props.caregiver.phoneNumbers[0].phoneNumber;
    const formatted = toNational(props.caregiver.phoneNumbers[0].phoneNumber);

    const replacePlaceholders = (value: string) => {
      return value.replace("{{name}}", params.name).replace("{{number}}", formatted);
    };

    const modal = createModal({
      id: "call-number",
      type: "neutral",
      title: replacePlaceholders(translate(STRINGS.OUTBOUND_CALL_PROMPT_TITLE)),
      message: replacePlaceholders(translate(STRINGS.OUTBOUND_CALL_PROMPT_MESSAGE)),
      actions: [
        {
          children: "Call",
          key: "call",
          onClick: () => {
            modal.update({ isLoading: true });
            call.mutate(
              { source: source, destination: params.destination },
              {
                onSuccess: () => {
                  modal.update({
                    title: replacePlaceholders(translate(STRINGS.OUTBOUND_CALL_SUCCESS_TITLE)),
                    message: replacePlaceholders(translate(STRINGS.OUTBOUND_CALL_SUCCESS_MESSAGE)),
                    isLoading: false,
                    actions: [
                      ModalActions.Close({
                        onClick: () => {
                          modal.remove();
                          params.onClickCall?.();
                        },
                      }),
                    ],
                  });
                },
                onError: () => {
                  modal.update({
                    title: replacePlaceholders(translate(STRINGS.OUTBOUND_CALL_ERROR_TITLE)),
                    message: replacePlaceholders(translate(STRINGS.OUTBOUND_CALL_ERROR_MESSAGE)),
                    isLoading: false,
                    actions: [
                      ModalActions.Close({
                        onClick: () => {
                          modal.remove();
                          params.onClickCancel?.();
                        },
                      }),
                    ],
                  });
                },
              }
            );
          },
        },
        ModalActions.Close({
          children: translate(STRINGS.CANCEL),
          onClick: () => {
            modal.remove();
            params.onClickCancel?.();
          },
        }),
      ],
    });
  };
}
